import { useState } from 'react';

export default function FooterCTA() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('company', formData.company);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message || '');

      const response = await fetch('https://readdy.ai/api/form/d65aj2e5t3g9njrsekog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ company: '', name: '', email: '', phone: '', message: '' });
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="contact-form" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Message Area */}
          <div>
            <p className="text-base text-[#4a4a4a] mb-6 leading-relaxed">
              まずは、標準業務に<span className="font-bold text-[#5a5a6e]">「Core」</span>を。<br />
              成果を出してほしい社員に<span className="font-bold text-[#8E7CC3]">「Pro」</span>を。<br />
              そして、職場全体の健康を<span className="font-bold text-[#5a8a6a]">「Style」</span>で底上げしてください。
            </p>
            <h2 className="text-4xl font-bold mb-8 leading-tight text-[#1a1a2e]" style={{ fontFamily: 'Noto Serif JP, serif' }}>
              御社に最適な<br />
              オフィスチェア戦略を<br />
              ご提案いたします。
            </h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-[#8E7CC3] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <span className="text-sm text-[#4a4a4a]">30秒で完了する簡単フォーム</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-[#8E7CC3] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <span className="text-sm text-[#4a4a4a]">24時間以内に担当者からご連絡</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-[#8E7CC3] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <span className="text-sm text-[#4a4a4a]">無料見積もり・導入プラン作成</span>
              </div>
            </div>
          </div>

          {/* Right Form Area */}
          <div className="bg-[#fafafa] rounded-2xl shadow-xl p-8 border border-[#e8e6e3]">
            <h3 className="text-2xl font-bold mb-6 text-[#1a1a2e]">法人見積もり・お問い合わせ</h3>
            
            <form onSubmit={handleSubmit} data-readdy-form id="corporate-inquiry-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#1a1a2e] mb-2">会社名 *</label>
                  <input
                    type="text"
                    name="company"
                    required
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    className="w-full px-4 py-3 border-b-2 border-[#e8e6e3] focus:border-[#8E7CC3] outline-none transition-colors bg-transparent text-sm"
                    placeholder="株式会社〇〇"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a1a2e] mb-2">担当者名 *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full px-4 py-3 border-b-2 border-[#e8e6e3] focus:border-[#8E7CC3] outline-none transition-colors bg-transparent text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a1a2e] mb-2">メールアドレス *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full px-4 py-3 border-b-2 border-[#e8e6e3] focus:border-[#8E7CC3] outline-none transition-colors bg-transparent text-sm"
                    placeholder="example@company.co.jp"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a1a2e] mb-2">電話番号 *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="w-full px-4 py-3 border-b-2 border-[#e8e6e3] focus:border-[#8E7CC3] outline-none transition-colors bg-transparent text-sm"
                    placeholder="03-1234-5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a1a2e] mb-2">お問い合わせ内容</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        handleChange('message', e.target.value);
                      }
                    }}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-[#e8e6e3] rounded-lg focus:border-[#8E7CC3] outline-none transition-colors bg-transparent text-sm resize-none"
                    placeholder="導入予定人数、ご希望の納期など"
                  />
                  <div className="text-xs text-[#4a4a4a] mt-1 text-right">{formData.message.length}/500文字</div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#8E7CC3] text-white py-4 rounded-full text-base font-bold hover:bg-[#7A6AAF] transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  <span>{isSubmitting ? '送信中...' : '無料見積もりを依頼する'}</span>
                  {!isSubmitting && <i className="ri-arrow-right-line text-xl"></i>}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-[#5a8a6a]/10 border border-[#5a8a6a] rounded-lg p-4 text-center animate-fade-in">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <i className="ri-checkbox-circle-fill text-2xl text-[#5a8a6a]"></i>
                      <p className="text-base text-[#5a8a6a] font-bold">送信完了</p>
                    </div>
                    <p className="text-sm text-[#5a8a6a]">お問い合わせありがとうございます。<br />担当者より24時間以内にご連絡いたします。</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-[#e07856]/10 border border-[#e07856] rounded-lg p-4 text-center animate-fade-in">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <i className="ri-error-warning-fill text-2xl text-[#e07856]"></i>
                      <p className="text-base text-[#e07856] font-bold">送信エラー</p>
                    </div>
                    <p className="text-sm text-[#e07856]">送信に失敗しました。<br />もう一度お試しください。</p>
                  </div>
                )}
              </div>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => window.open('https://calendly.com/bitmoves_zenko/calendly', '_blank', 'width=800,height=700')}
                className="text-sm text-[#8E7CC3] hover:underline cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-calendar-check-line"></i>
                まずはお打ち合わせをご希望の方はこちら
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
